import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { Breadcrumbs, H5 } from '../../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Container, Row, Input, FormGroup, Label } from 'reactstrap';
import DataTable from '../../../Common/DataTable';
import { PlusCircle, Edit, User, File, Trash2 } from 'react-feather';
import PaymentCollectionDataModal from './PaymentCollectionDataModal';
import ExpenseDataModal from '../../FinancialOperations/Expenses/ExpenseDataModal';
import DeleteConfirmationModal from '../../../Common/Modals/DeleteConfirmationModal';
import { confirmDelete, fetchTableData } from '../../../../Api/ApiMethods';
import { useNavigate } from 'react-router-dom';

const PaymentCollections = () => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [clearSelectedRows, setClearSelectedRows] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [startDateSearch, setStartDateSearch] = useState('');
    const [endDateSearch, setEndDateSearch] = useState('');
    const [totalRows, setTotalRows] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedPaymentCollection, setSelectedPaymentCollection] = useState(null);
    const [expenseDetailModalOpen, setExpenseDetailModalOpen] = useState(false);
    const [selectedExpenseForView, setSelectedExpenseForView] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    const paymentCollectionApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/paymentcollection';
    const expenseApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/expense';

    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    const handleStartDateChange = (event) => {
        setStartDateSearch(event.target.value);
        setPage(1);
    };

    const handleEndDateChange = (event) => {
        setEndDateSearch(event.target.value);
        setPage(1);
    };

    useEffect(() => {
        let isMounted = true;
        const loadPaymentCollections = async () => {
            setIsLoading(true);
            try {
                const queryParams = new URLSearchParams();
                if (startDateSearch) {
                    queryParams.append('startDate', startDateSearch);
                }
                if (endDateSearch) {
                    queryParams.append('endDate', endDateSearch);
                }

                const apiUrl = queryParams.toString()
                    ? `${paymentCollectionApiUrl}?${queryParams.toString()}`
                    : paymentCollectionApiUrl;

                await fetchTableData({
                    apiEndpoint: apiUrl,
                    page,
                    pageSize,
                    search: null,
                    setDataCallback: setData,
                    setTotalRowsCallback: setTotalRows,
                });
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        loadPaymentCollections();

        return () => {
            isMounted = false;
        };
    }, [page, pageSize, startDateSearch, endDateSearch, paymentCollectionApiUrl]);

    const toggleDeleteModal = () => setDeleteModal(!deleteModal);

    const toggleModal = () => {
        setModalOpen(!modalOpen);
        if (modalOpen) {
            setTimeout(() => setSelectedPaymentCollection(null), 300);
        }
    };

    const openAddModal = () => {
        setModalMode('add');
        setSelectedPaymentCollection(null);
        setModalOpen(true);
    };

    const openModifyModal = (paymentCollection) => {
        setModalMode('modify');
        setSelectedPaymentCollection(paymentCollection);
        setModalOpen(true);
    };

    const toggleExpenseDetailModal = () => {
        setExpenseDetailModalOpen(!expenseDetailModalOpen);
        if (expenseDetailModalOpen) {
            setSelectedExpenseForView(null);
        }
    };

    const handleCallForm = (row) => {
        const callFormId = row.callForm?.[0];
        
        if (callFormId) {
            navigate(`/callform/${callFormId}`);
        } else {
            console.warn("Call form ID not found or structure unexpected in row:", row);
        }
    };

    const handleCustomerDetail = (customerId) => {
        navigate(`/customer/${customerId}`);
    };

    const renderCellWithTooltip = (row, field, defaultValue = '') => {
        const value = row[field] || defaultValue;
        return (
            <div
                title={value}
                style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '150px',
                }}
            >
                {value}
            </div>
        );
    };

    const columns = [
        { name: 'Tahsilat No', selector: row => row.paymentCollectionNumber || '', sortable: true, width: '150px' },
        { name: 'Tahsilat Tipi', selector: row => row.paymentCollectionType || '', sortable: true, width: '150px' },
        { name: 'Tutar', selector: row => row.amount ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(row.amount) : '', sortable: true, width: '120px' },
        { name: 'Tarih ve Saat', selector: row => row.date ? new Date(row.date).toLocaleString() : '', sortable: true, width: '180px' },
        { name: 'Ödeme Yöntemi', selector: row => row.paymentMethod || '', sortable: true, width: '150px' },
        { name: 'Makbuz No', selector: row => row.receiptNumber || '', sortable: true, cell: row => renderCellWithTooltip(row, 'receiptNumber'), width: '130px' },
        {
            name: 'Masraf No',
            selector: row => row.expense?.expenseNumber || '-',
            sortable: true,
            cell: row => renderCellWithTooltip(row.expense || {}, 'expenseNumber', '-'),
            width: '130px'
        },
        { name: 'Açıklama', selector: row => row.description || '', sortable: true, cell: row => renderCellWithTooltip(row, 'description'), grow: 2 },
        {
            name: 'Müşteri',
            cell: row => {
                const customerId = row.customer?.[0];
                return customerId ? (
                    <div className="btn btn-outline-primary" onClick={() => handleCustomerDetail(customerId)} title="Müşteri Detayı">
                        <User size={18}/>
                    </div>
                ) : '-';
            },
            ignoreRowClick: true,
            right: true,
            button: true,
        },
        {
            name: 'Çağrı Formu',
            cell: row => {
                const callFormId = row.callForm?.[0];
                return callFormId ? (
                    <div className="btn btn-outline-info" onClick={() => handleCallForm(row)} title="Kaza Dosyasına Git">
                        <File size={18}/>
                    </div>
                ) : '-';
            },
            ignoreRowClick: true,
            right: true,
            button: true,
        },
        {
            name: 'Düzenle',
            cell: (row) => (
                <div
                    className="btn btn-outline-warning"
                    onClick={() => openModifyModal(row)}
                    title="Düzenle"
                >
                    <Edit size={18} />
                </div>
            ),
            ignoreRowClick: true,
            right: true,
            button: true,
        },
    ];

    return (
        <>
            <Breadcrumbs parent='Sayfalar' title='Tahsilatlar' />
            <Container fluid={true}>
                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardHeader>
                                <H5>Tahsilatlar</H5>
                                <span>Mevcut olan tahsilatlar aşağıda listelenmektedir.</span>
                            </CardHeader>
                            <CardBody>
                                <Row className="g-3 mb-3 align-items-center">
                                    <Col md="3" sm="6">
                                        <FormGroup>
                                            <Label>Arama Başlangıç Tarihi</Label>
                                            <Input
                                                type="date"
                                                value={startDateSearch}
                                                onChange={handleStartDateChange}
                                                max={endDateSearch || undefined}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3" sm="6">
                                        <FormGroup>
                                            <Label>Arama Bitiş Tarihi</Label>
                                            <Input
                                                type="date"
                                                value={endDateSearch}
                                                onChange={handleEndDateChange}
                                                min={startDateSearch || undefined}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3" sm="6" />
                                    <Col md="3" sm="6" className="d-flex justify-content-end align-items-center">
                                        <div className="btn btn-outline-primary ms-auto" onClick={openAddModal} style={{ height: '38px', display: 'inline-flex', alignItems: 'center' }}>
                                            <PlusCircle size={18} className="me-1"/>
                                            {' Tahsilat Ekle'}
                                        </div>
                                        {(selectedRows.length !== 0) &&
                                            <div className='btn btn-outline-danger ms-2' onClick={toggleDeleteModal} style={{ height: '38px', display: 'inline-flex', alignItems: 'center' }}>
                                                <Trash2 size={18} className="me-1"/>
                                                {' Seçilenleri Sil'}
                                            </div>
                                        }
                                    </Col>
                                </Row>

                                <div style={{ height: "40px" }}></div>

                                <Fragment>
                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        onSelectedRowsChange={handleRowSelected}
                                        pagination
                                        paginationServer
                                        selectableRows
                                        clearSelectedRows={clearSelectedRows}
                                        paginationTotalRows={totalRows}
                                        onChangePage={page => setPage(page)}
                                        onChangeRowsPerPage={pageSize => { setPage(1); setPageSize(pageSize); }}
                                        progressPending={isLoading}
                                        progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                                        noDataComponent={isLoading ? null : <div className="p-4 text-center">Tahsilat bulunamadı.</div>}
                                    />
                                </Fragment>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <PaymentCollectionDataModal
                apiEndpoint={paymentCollectionApiUrl}
                isOpen={modalOpen}
                toggle={toggleModal}
                mode={modalMode}
                selectedPaymentCollection={selectedPaymentCollection}
                refreshData={() =>
                    fetchTableData({
                        apiEndpoint: (() => {
                            const queryParams = new URLSearchParams();
                            if (startDateSearch) {
                                queryParams.append('startDate', startDateSearch);
                            }
                            if (endDateSearch) {
                                queryParams.append('endDate', endDateSearch);
                            }
                            return queryParams.toString()
                                ? `${paymentCollectionApiUrl}?${queryParams.toString()}`
                                : paymentCollectionApiUrl;
                        })(),
                            page,
                            pageSize,
                            search: null,
                        setDataCallback: setData,
                        setTotalRowsCallback: setTotalRows,
                    })
                }
            />
            {selectedExpenseForView && expenseDetailModalOpen && (
                 <ExpenseDataModal
                    apiEndpoint={expenseApiUrl} 
                    isOpen={expenseDetailModalOpen}
                    toggle={toggleExpenseDetailModal}
                    mode="modify"
                    selectedExpense={selectedExpenseForView}
                    refreshData={() => { }}
                    readOnlyView={true}
                 />
            )}
            <DeleteConfirmationModal
                isOpen={deleteModal}
                toggle={toggleDeleteModal}
                itemsToDelete={selectedRows}
                displayKey="paymentCollectionNumber"
                onConfirm={() =>
                    confirmDelete({
                        apiEndpoint: paymentCollectionApiUrl,
                        selectedItems: selectedRows,
                        toggleModalCallback: toggleDeleteModal,
                        clearSelectionCallback: () => {
                            setSelectedRows([]);
                            setClearSelectedRows(!clearSelectedRows);
                        },
                        refreshDataCallback: () =>
                            fetchTableData({
                                apiEndpoint: (() => {
                                    const queryParams = new URLSearchParams();
                                    if (startDateSearch) {
                                        queryParams.append('startDate', startDateSearch);
                                    }
                                    if (endDateSearch) {
                                        queryParams.append('endDate', endDateSearch);
                                    }
                                    return queryParams.toString()
                                        ? `${paymentCollectionApiUrl}?${queryParams.toString()}`
                                        : paymentCollectionApiUrl;
                                })(),
                                    page,
                                    pageSize,
                                    search: null,
                                setDataCallback: setData,
                                setTotalRowsCallback: setTotalRows,
                            }),
                    })
                }
            />
        </>
    );
};

export default PaymentCollections;
